import React, { useState, useEffect, useMemo } from "react";
import {
  MainTableProps,
  TableColumnsItem,
  TableButtonItem,
} from "../../interfaces/components/mainTable";
import { computeRowSpans } from "../../shared/utils/mainTableHelper";
import Paper from "../paper";
import StatusChip from "../statusChip";
import TypeBadge from "../typeBadge";
import {
  Status,
  Priority,
  Type,
  StatusChipTypes,
} from "../../shared/utils/enums";
import {
  Plus,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreVertical,
  Trash2,
} from "lucide-react";
import Button from "../button";
import { ButtonVariant } from "../../shared/utils/enums";

const MainTable: React.FC<MainTableProps> = ({
  data = [],
  columns = [],
  onRowPress,
  onCheckedChange,
  allowMerge = false,
  tableClass = "",
  showRowHover = true,
  isLoading = false,
  showEmptyList = true,
  totalData = 0,
  pageSize = 10,
  currentPage = 0,
  showPagination = true,
  onPageChanged,
  searchQuery = "",
  onSearchChange,
  showSearch = true,
  addButtonLabel = "Add New",
  onAddNewItemPress,
  showAddButton = true,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const displayedColumns = useMemo(() => {
    return columns
      .filter((column) => !(column.hideOnMobile && isMobile))
      .map((item) => item.key);
  }, [columns, isMobile]);

  const rowSpans = useMemo(() => {
    return computeRowSpans(data, displayedColumns);
  }, [data, displayedColumns]);

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data;

    return data.filter((row) => {
      return Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase()),
      );
    });
  }, [data, searchQuery]);

  const getColumnValue = (row: any, key: string = "key"): any => {
    try {
      if (!key.includes(".")) return row[key];
      const paths = key.split(".");
      let value = row;
      paths.forEach((subKey: string) => {
        value = value[subKey];
      });
      return value;
    } catch (e) {
      console.error("get column value error =>", e);
      return "";
    }
  };

  const getValue = (
    row: any,
    column: TableColumnsItem,
    iRow: number,
    valueKey: string = "key",
  ): string => {
    if (column.type === "custom" && column.customValue) {
      return column.customValue(row, iRow);
    }
    const value =
      getColumnValue(
        row,
        column[valueKey as keyof TableColumnsItem] as string,
      ) || "";
    if (column.type === "countable" && Array.isArray(value))
      return value.length.toString();
    return value.toString();
  };

  const isText = (type: string): boolean => {
    return type === "text" || type === "countable" || type === "custom";
  };

  const toggleCheck = (
    event: React.MouseEvent,
    index: number,
    _iCol: number,
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    const currentValue = +filteredData[index][columns[_iCol].key];
    const newValue = !currentValue;
    filteredData[index][columns[_iCol].key] = newValue;
    onCheckedChange?.({ row: filteredData[index], value: newValue });
  };

  const onButtonPress = (
    event: React.MouseEvent,
    button: TableButtonItem,
    row: any,
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    if (button.onPress) {
      button.onPress(event, row);
    }
  };

  const renderCellContent = (
    row: any,
    column: TableColumnsItem,
    iRow: number,
    _iCol: number,
  ) => {
    if (column.type === "status") {
      return (
        <StatusChip
          status={getValue(row, column, iRow) as Status}
          type={StatusChipTypes.status}
        />
      );
    }

    if (column.type === "priority") {
      return (
        <StatusChip
          status={getValue(row, column, iRow) as Priority}
          type={StatusChipTypes.priority}
        />
      );
    }

    if (column.type === "type") {
      return <TypeBadge type={getValue(row, column, iRow) as Type} />;
    }

    if (column.type === "checkbox") {
      return (
        <input
          type="checkbox"
          checked={+row[column.key] === 1}
          onChange={(e) => toggleCheck(e as any, iRow, _iCol)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:checked:bg-blue-600"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (column.type === "button" && column.buttonsList) {
      return (
        <div className="flex items-center justify-end space-x-1">
          {column.buttonsList.map((button, btnIndex) => {
            if (
              button.type === "icon" &&
              (!button.enabledKey || row[button.enabledKey])
            ) {
              const isDeleteButton =
                button.cssClass &&
                (button.cssClass.includes("delete") ||
                  button.cssClass.includes("remove") ||
                  button.cssClass.includes("trash"));

              const IconComponent =
                button.icon || (isDeleteButton ? Trash2 : MoreVertical);

              return (
                <Button
                  key={btnIndex}
                  disabled={
                    !!button.disabled ||
                    (!!button.enabledKey && !!!row[button.enabledKey])
                  }
                  onClick={(e) => onButtonPress(e, button, row)}
                  variant={
                    isDeleteButton ? ButtonVariant.DANGER : ButtonVariant.CLEAR
                  }
                  className={`w-8 h-8 p-0 flex items-center justify-center ${!!button.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${isDeleteButton ? "hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20" : ""} ${button.cssClass || ""}`}
                  aria-label={button.icon ? button.icon.name : "Action"}
                  type="button"
                >
                  <IconComponent className="w-4 h-4 text-gray-400 dark:text-gray-300" />
                </Button>
              );
            }

            if (button.type === "toggle") {
              return (
                <label
                  key={btnIndex}
                  className="relative inline-flex items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={+row[button.checkedKey || ""] === 1}
                    onChange={(e) => {
                      e.stopPropagation();
                      button.onChange?.(e, row);
                    }}
                    disabled={button.disabled}
                    className="sr-only"
                  />
                  <div
                    className={`
                      relative w-11 h-6 rounded-md transition-colors duration-200
                      ${+row[button.checkedKey || ""] === 1 ? "bg-blue-600" : "bg-gray-200 dark:bg-gray-700"}
                      ${button.disabled ? "opacity-50 cursor-not-allowed" : ""}
                    `}
                  >
                    <div
                      className={`
                        absolute top-0.5 left-0.5 bg-white dark:bg-gray-300 w-5 h-5 rounded-md transition-transform duration-200
                        ${+row[button.checkedKey || ""] === 1 ? "translate-x-5" : "translate-x-0"}
                      `}
                    />
                  </div>
                </label>
              );
            }

            return null;
          })}
        </div>
      );
    }

    if (column.type === "image") {
      return (
        <img
          src={getValue(row, column, iRow)}
          alt={column.key}
          className="object-contain rounded"
          style={{
            width: column.imageWidth || "32px",
            height: column.imageWidth || "32px",
            background: getValue(row, column, iRow, "imageBackgroundKey"),
          }}
        />
      );
    }

    if (isText(column.type)) {
      return (
        <span
          className={
            column.key === "title"
              ? "text-gray-900 dark:text-white font-medium"
              : "text-gray-600 dark:text-gray-300"
          }
        >
          {getValue(row, column, iRow) || "—"}
        </span>
      );
    }

    return null;
  };

  const renderMobileCard = (row: any, index: number) => {
    const _titleColumn = columns.find((col) => col.cardTitle);
    const _titleValue = _titleColumn
      ? getValue(row, _titleColumn, index)
      : `Item ${index + 1}`;

    const mobileFields = columns
      .filter((col) => !col.cardHide && !col.cardTitle)
      .sort((a, b) => (a.cardOrder || 999) - (b.cardOrder || 999));

    return (
      <div
        key={row.id || index}
        className="bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-800 p-4 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors duration-200 relative"
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white flex-1 pr-2">
            {_titleValue}
          </h3>
          {mobileFields.find((col) => col.type === "button") && (
            <div className="flex items-center space-x-1 flex-shrink-0 relative z-10">
              {mobileFields
                .filter((col) => col.type === "button")
                .map((column) => (
                  <div key={column.key}>
                    {renderCellContent(row, column, index, 0)}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          {mobileFields
            .filter((col) => col.type !== "button")
            .map((column, fieldIndex) => (
              <div
                key={column.key}
                className="flex items-center justify-between"
              >
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {column.name}
                </span>
                <div className="flex-shrink-0">
                  {renderCellContent(row, column, index, fieldIndex)}
                </div>
              </div>
            ))}
        </div>

        {onRowPress && (
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={() => onRowPress(row)}
            style={{ zIndex: 1 }}
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={tableClass}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          {showAddButton && onAddNewItemPress && (
            <Button
              onClick={onAddNewItemPress}
              variant={ButtonVariant.PRIMARY}
              className="w-full md:w-auto px-3 py-1.5 text-white rounded-lg flex items-center justify-center gap-1.5 transition-colors duration-200 text-sm"
            >
              <Plus className="w-4 h-4 text-white mr-2" />
              {addButtonLabel}
            </Button>
          )}
          {onSearchChange && showSearch && (
            <div className="relative w-full md:w-80">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                disabled={true}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 bg-transparent dark:border-gray-700 bg-transparent dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed dark:text-white dark:placeholder-gray-400"
              />
              <Search className="w-4 h-4 text-gray-400 dark:text-gray-300 absolute left-2.5 top-2" />
            </div>
          )}
        </div>

        {isMobile ? (
          <div className="space-y-4">
            {[...Array(6)].map((_, index) => {
              const _titleColumn = columns.find((col) => col.cardTitle);
              const mobileFields = columns
                .filter((col) => !col.cardHide && !col.cardTitle)
                .sort((a, b) => (a.cardOrder || 999) - (b.cardOrder || 999));

              return (
                <div
                  key={index}
                  className="bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-800 p-4 animate-pulse"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="h-5 bg-gray-200 dark:bg-gray-700 rounded flex-1 mr-2"
                      style={{ animationDelay: `${index * 100}ms` }}
                    />
                    {mobileFields.find((col) => col.type === "button") && (
                      <div className="flex space-x-1 flex-shrink-0">
                        {mobileFields
                          .filter((col) => col.type === "button")
                          .map((column, btnIndex) => (
                            <div key={column.key} className="flex space-x-1">
                              {column.buttonsList?.map((_, buttonIndex) => (
                                <div
                                  key={buttonIndex}
                                  className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-md"
                                  style={{
                                    animationDelay: `${index * 100 + btnIndex * 50 + buttonIndex * 25}ms`,
                                  }}
                                />
                              ))}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {mobileFields
                      .filter((col) => col.type !== "button")
                      .map((column, fieldIndex) => {
                        const getSkeletonForType = () => {
                          switch (column.type) {
                            case "status":
                              return (
                                <div className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded-md" />
                              );
                            case "priority":
                              return (
                                <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-md" />
                              );
                            case "type":
                              return (
                                <div className="h-6 w-18 bg-gray-200 dark:bg-gray-700 rounded-md" />
                              );
                            case "checkbox":
                              return (
                                <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded-md" />
                              );
                            default:
                              return (
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
                              );
                          }
                        };

                        return (
                          <div
                            key={column.key}
                            className="flex items-center justify-between"
                          >
                            <div
                              className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"
                              style={{
                                animationDelay: `${index * 100 + fieldIndex * 50}ms`,
                              }}
                            />
                            <div
                              className="flex-shrink-0"
                              style={{
                                animationDelay: `${index * 100 + fieldIndex * 50 + 25}ms`,
                              }}
                            >
                              {getSkeletonForType()}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Paper
            elevation={0}
            className="border border-gray-200 dark:border-gray-800 overflow-hidden bg-transparent"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary/20 dark:bg-darkCardBackground/50 border-b border-secondary/20 dark:border-gray-700">
                  <tr className="h-12">
                    {columns.map((column, _iCol) => {
                      if (column.hideOnMobile && isMobile) return null;
                      return (
                        <th
                          key={column.key}
                          className={`px-4 py-3 text-left text-xs font-semibold text-primary dark:text-gray-200 uppercase tracking-wider ${column.centerText ? "text-center" : ""}`}
                          style={{
                            width:
                              isMobile && column.mobileWidth
                                ? column.mobileWidth
                                : column.width,
                          }}
                        >
                          {column.name}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody className="bg-white dark:bg-black divide-y divide-gray-100 dark:divide-gray-800">
                  {[...Array(8)].map((_, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="hover:bg-gray-50 dark:hover:bg-gray-900"
                    >
                      {columns.map((column, _iCol) => {
                        if (column.hideOnMobile && isMobile) return null;

                        const getSkeletonForType = () => {
                          switch (column.type) {
                            case "status":
                              return (
                                <div className="animate-pulse bg-gray-100 dark:bg-darkCardBackground h-6 w-20 rounded-md" />
                              );
                            case "priority":
                              return (
                                <div className="animate-pulse bg-gray-100 dark:bg-darkCardBackground h-6 w-16 rounded-md" />
                              );
                            case "type":
                              return (
                                <div className="animate-pulse bg-gray-100 dark:bg-darkCardBackground h-6 w-18 rounded-md" />
                              );
                            case "checkbox":
                              return (
                                <div className="animate-pulse bg-gray-100 dark:bg-darkCardBackground h-4 w-4 rounded-md" />
                              );
                            case "button":
                              return (
                                <div className="flex justify-end space-x-1">
                                  {column.buttonsList?.map((_, btnIndex) => (
                                    <div
                                      key={btnIndex}
                                      className="animate-pulse bg-gray-100 dark:bg-darkCardBackground h-8 w-8 rounded-md"
                                      style={{
                                        animationDelay: `${(rowIndex * columns.length + _iCol) * 50 + btnIndex * 25}ms`,
                                      }}
                                    />
                                  ))}
                                </div>
                              );
                            default:
                              return (
                                <div
                                  className="animate-pulse bg-gray-100 dark:bg-darkCardBackground h-4 rounded-md"
                                  style={{
                                    width:
                                      column.key === "title"
                                        ? "90%"
                                        : column.key === "tasks"
                                          ? "60px"
                                          : "70%",
                                  }}
                                />
                              );
                          }
                        };

                        return (
                          <td
                            key={`${rowIndex}-${column.key}`}
                            className={`px-4 py-3 text-xs ${column.centerText ? "text-center" : "text-left"} text-gray-600 dark:text-gray-300`}
                            style={{
                              animationDelay: `${(rowIndex * columns.length + _iCol) * 50}ms`,
                            }}
                          >
                            {getSkeletonForType()}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {showPagination && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-black flex items-center justify-between">
                <div className="animate-pulse">
                  <div
                    className="bg-gray-100 dark:bg-darkCardBackground h-4 w-32 rounded-md"
                    style={{ animationDelay: "800ms" }}
                  />
                </div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2 animate-pulse">
                    <div
                      className="bg-gray-100 dark:bg-darkCardBackground h-4 w-20 rounded-md"
                      style={{ animationDelay: "900ms" }}
                    />
                    <div
                      className="bg-gray-100 dark:bg-darkCardBackground h-8 w-16 rounded-md"
                      style={{ animationDelay: "1000ms" }}
                    />
                  </div>
                  <div className="animate-pulse">
                    <div
                      className="bg-gray-100 dark:bg-darkCardBackground h-4 w-20 rounded-md"
                      style={{ animationDelay: "1100ms" }}
                    />
                  </div>
                  <div className="flex items-center space-x-1">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="animate-pulse bg-gray-100 dark:bg-darkCardBackground h-8 w-8 rounded-md"
                        style={{ animationDelay: `${1200 + i * 100}ms` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Paper>
        )}
      </div>
    );
  }

  if (!filteredData.length && showEmptyList) {
    return (
      <div className={tableClass}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          {showAddButton && onAddNewItemPress && (
            <Button
              onClick={onAddNewItemPress}
              variant={ButtonVariant.PRIMARY}
              className="w-full md:w-auto px-3 py-1.5 text-white rounded-lg flex items-center justify-center gap-1.5 transition-colors duration-200 text-sm"
            >
              <Plus className="w-4 h-4 text-white mr-2" />
              {addButtonLabel}
            </Button>
          )}
          {onSearchChange && showSearch && (
            <div className="relative w-full md:w-80">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 bg-transparent dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white dark:placeholder-gray-400"
              />
              <Search className="w-4 h-4 text-gray-400 dark:text-gray-300 absolute left-2.5 top-2" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-center py-16">
          <div className="text-center text-gray-500 dark:text-gray-400">
            <div className="text-6xl mb-4">📋</div>
            <p className="text-lg font-medium">
              {searchQuery
                ? `No results found for "${searchQuery}"`
                : "No data available"}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              {searchQuery
                ? "Try adjusting your search terms"
                : "Add some data to get started"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={tableClass}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        {showAddButton && onAddNewItemPress && (
          <Button
            onClick={onAddNewItemPress}
            variant={ButtonVariant.PRIMARY}
            className="w-full md:w-auto px-3 py-1.5 text-white rounded-lg flex items-center justify-center gap-1.5 transition-colors duration-200 text-sm"
          >
            <Plus className="w-4 h-4 text-white mr-2" />
            {addButtonLabel}
          </Button>
        )}
        {onSearchChange && showSearch && (
          <div className="relative w-full md:w-80">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 bg-transparent dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white dark:placeholder-gray-400"
            />
            <Search className="w-4 h-4 text-gray-400 dark:text-gray-300 absolute left-2.5 top-2" />
          </div>
        )}
      </div>

      {isMobile ? (
        <div className="space-y-4">
          {filteredData.map((row, index) => renderMobileCard(row, index))}
        </div>
      ) : (
        <Paper
          elevation={0}
          className="border border-gray-200 dark:border-gray-800 overflow-hidden bg-transparent"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/20 dark:bg-darkCardBackground/50 border-b border-secondary/20 dark:border-gray-700">
                <tr className="h-12">
                  {columns.map((column, _iCol) => {
                    if (column.hideOnMobile && isMobile) return null;
                    return (
                      <th
                        key={column.key}
                        className={`px-4 py-3 text-left text-xs font-semibold text-primary dark:text-gray-200 uppercase tracking-wider ${column.centerText ? "text-center" : ""}`}
                        style={{
                          width:
                            isMobile && column.mobileWidth
                              ? column.mobileWidth
                              : column.width,
                        }}
                      >
                        {column.name}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-black divide-y divide-gray-100 dark:divide-gray-800">
                {filteredData.map((row, iRow) => (
                  <tr
                    key={row.id || iRow}
                    className={`
                        transition-colors duration-150
                        ${showRowHover && !allowMerge ? "hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer" : ""}
                      `}
                    onClick={() => onRowPress?.(row)}
                  >
                    {columns.map((column, _iCol) => {
                      if (column.hideOnMobile && isMobile) return null;

                      const spanInfo = allowMerge
                        ? rowSpans[_iCol]?.[iRow]
                        : { span: 1 };
                      if (spanInfo?.span === 0) return null;

                      return (
                        <td
                          key={column.key}
                          className={`px-4 py-3 text-xs ${column.centerText ? "text-center" : "text-left"} text-gray-600 dark:text-gray-300`}
                          rowSpan={allowMerge ? spanInfo?.span : 1}
                        >
                          {renderCellContent(row, column, iRow, _iCol)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredData.length > 0 && !isLoading && showPagination && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-black flex items-center justify-between">
              <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                <span>
                  Showing {currentPage * pageSize + 1} to{" "}
                  {Math.min((currentPage + 1) * pageSize, totalData)} of{" "}
                  {totalData} results
                </span>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Rows per page
                  </span>
                  <div className="relative">
                    <select
                      value={pageSize}
                      onChange={(e) =>
                        onPageChanged?.({
                          pageIndex: 0,
                          pageSize: parseInt(e.target.value),
                        })
                      }
                      className="appearance-none bg-white dark:bg-darkCardBackground border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 pr-8 text-xs text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 focus:border-blue-500 cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-150"
                    >
                      {[10, 25, 50].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 dark:text-gray-400">
                      <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-300 transition-transform duration-200" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                  <span>
                    Page {currentPage + 1} of {Math.ceil(totalData / pageSize)}
                  </span>
                </div>

                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => onPageChanged?.({ pageIndex: 0, pageSize })}
                    disabled={currentPage === 0}
                    className="p-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-500 dark:text-gray-400"
                    title="First page"
                  >
                    <ChevronsLeft className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                  </button>
                  <button
                    onClick={() =>
                      onPageChanged?.({ pageIndex: currentPage - 1, pageSize })
                    }
                    disabled={currentPage === 0}
                    className="p-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-500 dark:text-gray-400"
                    title="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                  </button>
                  <button
                    onClick={() =>
                      onPageChanged?.({ pageIndex: currentPage + 1, pageSize })
                    }
                    disabled={
                      currentPage >= Math.ceil(totalData / pageSize) - 1
                    }
                    className="p-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-500 dark:text-gray-400"
                    title="Next page"
                  >
                    <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                  </button>
                  <button
                    onClick={() =>
                      onPageChanged?.({
                        pageIndex: Math.ceil(totalData / pageSize) - 1,
                        pageSize,
                      })
                    }
                    disabled={
                      currentPage >= Math.ceil(totalData / pageSize) - 1
                    }
                    className="p-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-gray-500 dark:text-gray-400"
                    title="Last page"
                  >
                    <ChevronsRight className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </Paper>
      )}

      {isMobile && filteredData.length > 0 && !isLoading && showPagination && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {currentPage * pageSize + 1}–
            {Math.min((currentPage + 1) * pageSize, totalData)} of {totalData}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() =>
                onPageChanged?.({ pageIndex: currentPage - 1, pageSize })
              }
              disabled={currentPage === 0}
              className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-black border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() =>
                onPageChanged?.({ pageIndex: currentPage + 1, pageSize })
              }
              disabled={currentPage >= Math.ceil(totalData / pageSize) - 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-black border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainTable;
